import { TOKEN_LABELS, TOKEN_PATTERN, Token } from './expressionTokens';

/**
 * Découpage d'une formule en segments colorés, texte d'origine conservé à l'identique
 * (espaces, retours à la ligne, caractères inconnus compris) : le rendu se superpose
 * exactement au texte du champ de saisie.
 */
export type SegmentKind =
  | 'function'
  | 'variable'
  | 'number'
  | 'operator'
  | 'punctuation'
  | 'whitespace'
  | 'unknown';

export interface Segment {
  text: string;
  kind: SegmentKind;
  /** Code de la variable pour les segments `variable`. */
  code?: string;
}

// Même grammaire que le tokenizer, plus les espaces et le reste, pour ne rien perdre.
const SEGMENT_REGEX = new RegExp(
  `(\\d+\\.?\\d*|\\.\\d+)|(VAR:[a-zA-Z0-9_]+)|(MIN\\s*\\(|MAX\\s*\\(|IF\\s*\\()|(<=|>=|==|!=|<>|[+\\-*/=<>])|([();,])|(\\s+)|(.)`,
  'giy'
);
// Garde-fou : la grammaire ci-dessus doit rester celle du tokenizer.
const GRAMMAR_CHECK = new RegExp(`^(?:${TOKEN_PATTERN})$`, 'i');
['1.5', 'VAR:A', 'MIN(', 'IF (', '<=', '<>', ';'].forEach((sample) => {
  if (!GRAMMAR_CHECK.test(sample)) throw new Error(`expressionHighlight: grammaire désynchronisée sur « ${sample} »`);
});

export const segmentExpression = (expression: string): Segment[] => {
  const segments: Segment[] = [];
  SEGMENT_REGEX.lastIndex = 0;
  let match = SEGMENT_REGEX.exec(expression);
  while (match !== null) {
    const [text, number, variable, fn, operator, punctuation, whitespace] = match;
    if (number) segments.push({ text, kind: 'number' });
    else if (variable) segments.push({ text, kind: 'variable', code: text.slice(4) });
    else if (fn) segments.push({ text, kind: 'function' });
    else if (operator) segments.push({ text, kind: 'operator' });
    else if (punctuation) segments.push({ text, kind: 'punctuation' });
    else if (whitespace) segments.push({ text, kind: 'whitespace' });
    else segments.push({ text, kind: 'unknown' });
    if (SEGMENT_REGEX.lastIndex === expression.length) break;
    match = SEGMENT_REGEX.exec(expression);
  }
  return segments;
};

/**
 * Mise en forme : un facteur par ligne, coupée à chaque `*` de premier niveau, les
 * lignes suivantes indentées. Le texte reste une formule valide : les espaces et
 * retours à la ligne sont ignorés par le moteur et retirés à l'enregistrement.
 */
export const formatExpression = (expression: string): string => {
  const segments = segmentExpression(expression).filter((s) => s.kind !== 'whitespace');
  if (segments.length === 0) return '';

  const lines: string[] = [];
  let current = '';
  let depth = 0;
  segments.forEach((segment) => {
    if (segment.kind === 'function' || segment.text === '(') depth += 1;
    if (segment.text === ')') depth = Math.max(0, depth - 1);

    if (segment.kind === 'operator' && segment.text === '*' && depth === 0 && current !== '') {
      lines.push(current);
      current = '  * ';
      return;
    }
    if (segment.kind === 'function') {
      current += segment.text.replace(/\s+/g, '');
      return;
    }
    // Un espace après la virgule et autour des opérateurs binaires, pour respirer.
    if (segment.text === ',' || segment.text === ';') current += ', ';
    else if (segment.kind === 'operator') current += ` ${segment.text} `;
    else current += segment.text;
  });
  lines.push(current);
  return lines.map((line) => line.replace(/ +$/, '')).join('\n');
};

export type ReadingPart =
  | { kind: 'function' | 'operator' | 'punctuation' | 'number'; text: string }
  | { kind: 'variable'; code: string; label: string; known: boolean };

/** Lecture de la formule en mots : libellés de variables à la place des codes. */
export const readingParts = (
  tokens: Token[],
  labelOf: (code: string) => string | undefined
): ReadingPart[] =>
  tokens.map((token) => {
    if (token.kind === 'value') return { kind: 'number', text: token.value || '0' };
    if (token.kind === 'var') {
      const label = labelOf(token.code);
      return { kind: 'variable', code: token.code, label: label ?? token.code, known: label !== undefined };
    }
    const text = TOKEN_LABELS[token.kind];
    if (token.kind === 'if' || token.kind === 'min' || token.kind === 'max') {
      return { kind: 'function', text };
    }
    if (token.kind === 'lparen' || token.kind === 'rparen' || token.kind === 'sep') {
      return { kind: 'punctuation', text };
    }
    return { kind: 'operator', text };
  });
